import React from "react";
import { useAppSelector } from "store";

import { Box } from "@material-ui/core";
import { MapRef } from "react-map-gl";

import { selectPinnedLocation } from "modules/customer/tools/location/locationSlice";
import {
    selectIsLoading,
    selectHasErrors,
    selectClassifiedCompetitors
} from "modules/customer/tools/location/competitionNew/competitionSlice";
import { ClassifiedCompetitor } from "modules/customer/tools/location/competitionNew/competitor";
import { selectMapboxConfiguration } from "modules/siteConfiguration/siteConfigurationSlice";

import MapBase from "components/visuals/maps/MapBase";
import Explore from "pages/customer/tools/location/map/Explore";
import PinnedLocation from "pages/customer/tools/location/map/PinnedLocation";

import Legend from "./Legend";
import Marker from "./Marker";
import mathUtils from "utils/mathUtils";
import { numberSortExpression, SortDirection } from "utils/sortUtils";

const Map: React.FC = () => {
    const mapRef = React.useRef<MapRef>(null);
    const mapboxConfiguration = useAppSelector(selectMapboxConfiguration);
    const isLoading = useAppSelector(selectIsLoading);
    const hasErrors = useAppSelector(selectHasErrors);
    const competitors = useAppSelector(selectClassifiedCompetitors);
    const selectedLocation = useAppSelector(selectPinnedLocation);
    const [hoveredCompetitor, setHoveredCompetitor] = React.useState<ClassifiedCompetitor | undefined>(undefined);
    const [initialViewport, setInitialViewport] = React.useState({
        latitude: selectedLocation?.latitude ?? 0,
        longitude: selectedLocation?.longitude ?? 0,
        zoom: 13.5,
        minZoom: 4
    });

    React.useEffect(() => {
        setInitialViewport(current => {
            return {
                ...current,
                latitude: selectedLocation?.latitude ?? 0,
                longitude: selectedLocation?.longitude ?? 0
            };
        });
    }, [selectedLocation]);


    const downloadData = React.useMemo(() => {
        return competitors.map(competitor => {
            return {
                "Store name": competitor.storeName,
                "Brand name": competitor.brandName,
                "Distance from selected location (km)": mathUtils.round(competitor.distanceToSelectedLocation, 4),
                "Store category": competitor.displayCategoryName,
                "Alternate store categories": competitor.alternateCategoryNames,
                "Store size": competitor.sizeBucket?.label ?? "Unknown",
                "Size range (square feet)": competitor.sizeBucket ? `${competitor.sizeBucket.lowerBound} - ${competitor.sizeBucket.upperBound}` : "",
                "Competitor type": competitor.classification
            };
        }).sort((a, b) => numberSortExpression(a["Distance from selected location (km)"], b["Distance from selected location (km)"], SortDirection.ASC));
    }, [competitors]);

    return (
        <Box data-cy="competition-map">
            <MapBase
                loading={isLoading}
                error={hasErrors}
                mapboxAccessToken={mapboxConfiguration.accessToken}
                mapboxBaseMapStyle={mapboxConfiguration.baseMapStyle}
                height="100vh"
                initialViewport={initialViewport}
                addGeocoder={false}
                addNavigationControl={true}
                addRecenterButton={true}
                dataCy="competitors-map"
                mapRef={mapRef}
                downloadData={downloadData}
                addFullscreenButton={true}
            >
                <Explore />
                <PinnedLocation />
                {competitors?.map((competitor, index) =>
                    <Marker
                        key={index}
                        competitor={competitor}
                        onTooltipOpen={() => setHoveredCompetitor(competitor)}
                        onTooltipClose={() => setHoveredCompetitor(undefined)}
                    />
                )}
                <Legend
                    competitor={hoveredCompetitor}
                    selectedStore={selectedLocation}
                />
            </MapBase>
        </Box>
    );
};

export default Map;
